import {format, getMonth, getYear} from 'date-fns';
import {combine, merge, sample} from 'effector';
import {createGate} from 'effector-react';

import {showRestError} from '@app/errors';
import {rootDomain} from '@app/model';
import {DATE_REQUEST_PATTERN} from '@consts/date';
import {dateModel} from '@entities/date/model';
import * as invoicesApi from '@entities/invoices/api';
import {InvoiceType} from '@entities/invoices/types';
import {InnerSortings} from '@shared/types/grid';
import {mapOrders} from '@utils/grid';

import * as api from './api';

const domain = rootDomain.createDomain('TaxpayerDetails');

export const TaxpayerDetailsGate = createGate<{tin: string; isMainTab: boolean}>('TaxpayerDetails');
export const TaxpayerContractorsGate = createGate<{tin: string}>('TaxpayerContractors');
export const TaxpayerInvoicesGate = createGate<{tin: string}>('TaxpayerInvoices');
export const TaxpayerAccountGate = createGate<{tin: string}>('TaxpayerAccount');
export const TaxpayerDeclarationGate = createGate<{tin: string}>('TaxpayerDeclaration');

const fetchInfoFx = domain.createEffect(api.fetchTaxpayer);
const fetchBalanceFx = domain.createEffect(api.fetchTaxpayerYearBalance);
const fetchContractorsFx = domain.createEffect(api.fetchTaxpayerContractors);
const fetchInvoicesFx = domain.createEffect(invoicesApi.fetch);
const fetchAccountFx = domain.createEffect(api.fetchTaxpayerAccount);
const fetchDeclarationsFx = domain.createEffect(api.fetchTaxpayerDeclarations);

[
    fetchInfoFx,
    fetchBalanceFx,
    fetchContractorsFx,
    fetchInvoicesFx,
    // fetchAccountFx,
    fetchDeclarationsFx,
].forEach((fx) => fx.failData.watch(showRestError));

const selectedBalanceOptionChanged = domain.createEvent<'all' | 'accrual' | 'refund' | 'saldo'>();
const contractorViewChanged = domain.createEvent<'seller' | 'buyer'>();
const contractorSearchChanged = domain.createEvent<string>();
const contractorPageChanged = domain.createEvent<number>();
const contractorPageSizeChanged = domain.createEvent<number>();
const contractorSortingsChanged = domain.createEvent<InnerSortings>();
const invoiceViewChanged = domain.createEvent<'seller' | 'buyer'>();
const invoiceSearchChanged = domain.createEvent<string>();
const invoicePageChanged = domain.createEvent<number>();
const invoicePageSizeChanged = domain.createEvent<number>();
const invoiceSortingsChanged = domain.createEvent<InnerSortings>();
const declarationPageChanged = domain.createEvent<number>();
const declarationPageSizeChanged = domain.createEvent<number>();
const declarationSortingsChanged = domain.createEvent<InnerSortings>();

const info$ = domain
    .createStore<Awaited<ReturnType<typeof api.fetchTaxpayer>> | null>(null)
    .on(fetchInfoFx.doneData, (_, data) => data)
    .reset(TaxpayerDetailsGate.close);
const balance$ = domain
    .createStore<Awaited<ReturnType<typeof api.fetchTaxpayerYearBalance>> | null>(null)
    .on(fetchBalanceFx.doneData, (_, data) => data)
    .reset(TaxpayerDetailsGate.close);
const contractors$ = domain
    .createStore<Awaited<ReturnType<typeof api.fetchTaxpayerContractors>> | null>(null)
    .on(fetchContractorsFx.doneData, (_, data) => data)
    .reset(TaxpayerContractorsGate.close);
const invoices$ = domain
    .createStore<Awaited<ReturnType<typeof invoicesApi.fetch>> | null>(null)
    .on(fetchInvoicesFx.doneData, (_, data) => data)
    .reset(TaxpayerInvoicesGate.close);
const account$ = domain
    .createStore<Awaited<ReturnType<typeof api.fetchTaxpayerAccount>> | null>(null)
    .on(fetchAccountFx.doneData, (_, data) => data)
    .reset(TaxpayerAccountGate.close, TaxpayerDetailsGate.close);
const declarations$ = domain
    .createStore<Awaited<ReturnType<typeof api.fetchTaxpayerDeclarations>> | null>(null)
    .on(fetchDeclarationsFx.doneData, (_, data) => data)
    .reset(TaxpayerDeclarationGate.close);

const selectedBalanceOption$ = domain
    .createStore<'all' | 'accrual' | 'refund' | 'saldo'>('all')
    .on(selectedBalanceOptionChanged, (_, value) => value)
    .reset(TaxpayerDetailsGate.close);

const contractorView$ = domain
    .createStore<'seller' | 'buyer'>('seller')
    .on(contractorViewChanged, (_, value) => value);
const contractorSearch$ = domain.createStore('').on(contractorSearchChanged, (_, value) => value);
const contractorPage$ = domain.createStore(0).on(contractorPageChanged, (_, value) => value);
const contractorPageSize$ = domain
    .createStore(20)
    .on(contractorPageSizeChanged, (_, value) => value);
const contractorSortings$ = domain
    .createStore<InnerSortings>({})
    .on(contractorSortingsChanged, (_, value) => value);

const invoiceView$ = domain
    .createStore<'seller' | 'buyer'>('seller')
    .on(invoiceViewChanged, (_, value) => value);
const invoiceSearch$ = domain.createStore('').on(invoiceSearchChanged, (_, value) => value);
const invoicePage$ = domain.createStore(0).on(invoicePageChanged, (_, value) => value);
const invoicePageSize$ = domain.createStore(20).on(invoicePageSizeChanged, (_, value) => value);
const invoiceSortings$ = domain
    .createStore<InnerSortings>({invoiceDate: false})
    .on(invoiceSortingsChanged, (_, value) => value);
const invoiceTokensCache$ = domain.createStore<Record<number, string | null>>({0: null});
const invoiceTokensCacheChanged = domain.createEvent<{
    pageNumber: number;
    token: string | null;
}>();
const invoiceTokensCacheReset = domain.createEvent();

const declarationPage$ = domain.createStore(0).on(declarationPageChanged, (_, value) => value);
const declarationPageSize$ = domain
    .createStore(20)
    .on(declarationPageSizeChanged, (_, value) => value);
const declarationSortings$ = domain
    .createStore<InnerSortings>({correctNum: false})
    .on(declarationSortingsChanged, (_, value) => value);
const declarationTokensCache$ = domain.createStore<Record<number, string | null>>({0: null});
const declarationTokensCacheChanged = domain.createEvent<{
    pageNumber: number;
    token: string | null;
}>();
const declarationTokensCacheReset = domain.createEvent();

contractorPage$.reset(
    contractorViewChanged,
    contractorSearchChanged,
    contractorSortingsChanged,
    TaxpayerContractorsGate.close,
);
invoicePage$.reset(
    invoiceViewChanged,
    invoiceSearchChanged,
    invoiceSortingsChanged,
    TaxpayerInvoicesGate.close,
);
invoiceTokensCache$
    .on(invoiceTokensCacheReset, () => ({0: null}))
    .on(invoiceTokensCacheChanged, (state, {pageNumber, token}) => ({
        ...state,
        [pageNumber]: token,
    }));
declarationPage$.reset(
    declarationSortingsChanged,
    declarationPageSizeChanged,
    TaxpayerDeclarationGate.close,
);
declarationTokensCache$
    .on(declarationTokensCacheReset, () => ({0: null}))
    .on(declarationTokensCacheChanged, (state, {pageNumber, token}) => ({
        ...state,
        [pageNumber]: token,
    }));

const getPeriod = (range: {from: Date | null}) => format(range.from ?? new Date(), 'yyyy-MM');
const getAccountParams = (tin: string, range: {from: Date | null}) => {
    const current = range.from ?? new Date();
    return {tin, year: getYear(current), month: getMonth(current) + 1};
};

const getFiscalSort = (sortings: InnerSortings) => {
    const [sortBy, asc] = Object.entries(sortings)[0] ?? [];
    return sortBy ? {sortBy, direction: asc ? ('ASC' as const) : ('DESC' as const)} : {};
};

const getInvoiceDirection = (view: 'seller' | 'buyer'): InvoiceType =>
    view === 'seller' ? 'OUTGOING' : 'INCOMING';

sample({
    clock: merge([TaxpayerDetailsGate.open, dateModel.changed]),
    source: combine({date: dateModel.state, gate: TaxpayerDetailsGate.state}),
    filter: TaxpayerDetailsGate.status,
    fn: ({date, gate}) => ({tin: gate.tin, period: getPeriod(date.range)}),
    target: fetchInfoFx,
});

sample({
    clock: merge([TaxpayerDetailsGate.open, dateModel.changed]),
    source: combine({date: dateModel.state, gate: TaxpayerDetailsGate.state}),
    filter: TaxpayerDetailsGate.status,
    fn: ({date, gate}) => ({tin: gate.tin, year: getYear(date.range.from ?? new Date())}),
    target: fetchBalanceFx,
});

sample({
    clock: merge([
        TaxpayerContractorsGate.open,
        dateModel.changed,
        contractorViewChanged,
        contractorSearchChanged,
        contractorPageChanged,
        contractorPageSizeChanged,
        contractorSortingsChanged,
    ]),
    source: combine({
        gate: TaxpayerContractorsGate.state,
        date: dateModel.state,
        view: contractorView$,
        search: contractorSearch$,
        page: contractorPage$,
        pageSize: contractorPageSize$,
        sortings: contractorSortings$,
    }),
    filter: TaxpayerContractorsGate.status,
    fn: ({gate, date, view, search, page, pageSize, sortings}) => ({
        tin: gate.tin,
        period: getPeriod(date.range),
        view,
        page: page + 1,
        limit: pageSize,
        ...(search && {filter: search}),
        ...getFiscalSort(sortings),
    }),
    target: fetchContractorsFx,
});

sample({
    clock: merge([
        invoiceViewChanged,
        invoiceSearchChanged,
        invoicePageSizeChanged,
        invoiceSortingsChanged,
        dateModel.changed,
        TaxpayerInvoicesGate.close,
    ]),
    target: invoiceTokensCacheReset,
});

sample({
    clock: merge([
        declarationSortingsChanged,
        declarationPageSizeChanged,
        dateModel.changed,
        TaxpayerDeclarationGate.close,
    ]),
    target: declarationTokensCacheReset,
});

sample({
    clock: fetchInvoicesFx.doneData,
    source: invoicePage$,
    fn: (pageNumber, result) => ({
        pageNumber: pageNumber + 1,
        token: result.nextPageRef ?? null,
    }),
    target: invoiceTokensCacheChanged,
});

sample({
    clock: fetchDeclarationsFx.doneData,
    source: declarationPage$,
    fn: (pageNumber, result) => ({
        pageNumber: pageNumber + 1,
        token: result.nextPageRef ?? null,
    }),
    target: declarationTokensCacheChanged,
});

sample({
    clock: merge([
        TaxpayerInvoicesGate.open,
        dateModel.changed,
        invoiceViewChanged,
        invoiceSearchChanged,
        invoicePageChanged,
        invoicePageSizeChanged,
        invoiceSortingsChanged,
    ]),
    source: combine({
        gate: TaxpayerInvoicesGate.state,
        date: dateModel.state,
        view: invoiceView$,
        search: invoiceSearch$,
        page: invoicePage$,
        pageSize: invoicePageSize$,
        sortings: invoiceSortings$,
        cache: invoiceTokensCache$,
    }),
    filter: TaxpayerInvoicesGate.status,
    fn: ({gate, date, view, search, page, pageSize, sortings, cache}) => ({
        taxNumber: gate.tin,
        direction: getInvoiceDirection(view),
        paging: {
            pageSize,
            ...(cache?.[page] && {pageRef: cache[page]}),
        },
        dateFrom: date.range.from ? format(date.range.from, DATE_REQUEST_PATTERN) : '',
        dateTo: date.range.to ? format(date.range.to, DATE_REQUEST_PATTERN) : '',
        ...(search && {search}),
        ...(Object.keys(sortings).length && {order: mapOrders(sortings)}),
    }),
    target: fetchInvoicesFx,
});

sample({
    clock: merge([TaxpayerAccountGate.open, dateModel.changed]),
    source: combine({gate: TaxpayerAccountGate.state, date: dateModel.state}),
    filter: TaxpayerAccountGate.status,
    fn: ({gate, date}) => getAccountParams(gate.tin, date.range),
    target: fetchAccountFx,
});

sample({
    clock: merge([TaxpayerDetailsGate.open, dateModel.changed]),
    source: combine({gate: TaxpayerDetailsGate.state, date: dateModel.state}),
    filter: ({gate}) => gate.isMainTab,
    fn: ({gate, date}) => getAccountParams(gate.tin, date.range),
    target: fetchAccountFx,
});

sample({
    clock: merge([
        TaxpayerDeclarationGate.open,
        dateModel.changed,
        declarationPageChanged,
        declarationPageSizeChanged,
        declarationSortingsChanged,
    ]),
    source: combine({
        gate: TaxpayerDeclarationGate.state,
        date: dateModel.state,
        page: declarationPage$,
        pageSize: declarationPageSize$,
        sortings: declarationSortings$,
        cache: declarationTokensCache$,
    }),
    filter: TaxpayerDeclarationGate.status,
    fn: ({gate, date, page, pageSize, sortings, cache}) => {
        const current = date.range.from ?? new Date();
        return {
            inn: gate.tin,
            taxPeriod: `${(getMonth(current) + 1).toString().padStart(2, '0')}${getYear(current)}`,
            paging: {
                pageSize,
                ...(cache?.[page] && {pageRef: cache[page]}),
            },
            ...(Object.keys(sortings).length && {order: mapOrders(sortings)}),
        };
    },
    target: fetchDeclarationsFx,
});

export const taxpayerDetailsModel = {
    state: combine({
        info: info$,
        balance: balance$,
        selectedBalanceOption: selectedBalanceOption$,
        contractors: contractors$,
        contractorView: contractorView$,
        contractorSearch: contractorSearch$,
        contractorPage: contractorPage$,
        contractorPageSize: contractorPageSize$,
        contractorSortings: contractorSortings$,
        invoices: invoices$,
        invoiceView: invoiceView$,
        invoiceSearch: invoiceSearch$,
        invoicePage: invoicePage$,
        invoicePageSize: invoicePageSize$,
        invoiceSortings: invoiceSortings$,
        invoiceTokensCache: invoiceTokensCache$,
        account: account$,
        declarations: declarations$,
        declarationPage: declarationPage$,
        declarationPageSize: declarationPageSize$,
        declarationSortings: declarationSortings$,
        declarationTokensCache: declarationTokensCache$,
        pending: combine(
            [
                fetchInfoFx.pending,
                fetchBalanceFx.pending,
                fetchContractorsFx.pending,
                fetchInvoicesFx.pending,
                fetchAccountFx.pending,
                fetchDeclarationsFx.pending,
            ],
            (list) => list.some(Boolean),
        ),
    }),
    selectedBalanceOptionChanged,
    contractorViewChanged,
    contractorSearchChanged,
    contractorPageChanged,
    contractorPageSizeChanged,
    contractorSortingsChanged,
    invoiceViewChanged,
    invoiceSearchChanged,
    invoicePageChanged,
    invoicePageSizeChanged,
    invoiceSortingsChanged,
    declarationPageChanged,
    declarationPageSizeChanged,
    declarationSortingsChanged,
};
